/**
 * @file scan-payload.ts
 * @description Роутер скана склада на клиенте — зеркало серверного
 *   `apps/api/src/barcode/scan-payload.ts` (+ `internal-code.ts`, `gtin.ts`)
 *   (phase19.22, docs/business/inventory.md W17-1, W17-3, W17-4, W17-9a).
 *
 * Зачем файл существует: админка должна понять, ЧТО отсканировали, ещё до
 * запроса — иначе адрес ячейки уходит в поиск по артикулу, а этикетка товара
 * в фильтр строк сборки. Правило «что делает ввод» обязано совпадать с
 * сервером и мобильным (эталоны тестов — те же, что у API): разъедутся —
 * этикетка перестанет находить фасовку.
 *
 * Порядок разбора — W17-3 сверху вниз: `LOC:` → ячейка; `SKU:` → фасовка
 * (с `|` — этикетка с партией и сроком); `LOT:` → партия; `BOX:`/`ORD:`;
 * GS1-строка или `01`+14 цифр → «Честный знак»; 8/12/13/14 цифр с верной
 * контрольной → GTIN; иначе свободный текст.
 *
 * Уровень адреса (ячейка / полка / стеллаж) здесь НЕ определяется: его знает
 * справочник ячеек склада (W14-9), клиент только отправляет строку на
 * `POST /admin/wms/scan/resolve`.
 */

/** Реестр префиксов скана (W17-3). Новый префикс — новая запись здесь. */
export const SCAN_PREFIX = {
  SKU: "SKU:",
  LOC: "LOC:",
  LOT: "LOT:",
  BOX: "BOX:",
  ORD: "ORD:",
} as const;

/** Префикс срока — только внутри нагрузки этикетки. */
const EXP_PREFIX = "EXP:";

/** Разделитель сегментов нагрузки этикетки. */
const PAYLOAD_SEPARATOR = "|";

/** Групповой разделитель GS1 (FNC1 в DataMatrix «Честного знака»). */
const GS = "\x1d";

/** Код партии `Lot.code`: `L` + не меньше 7 цифр. */
const LOT_CODE_RE = /^L\d{7,15}$/;

/** Срок в нагрузке: календарная дата ISO. */
const EXP_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Ведущие GS: часть сканеров отдаёт FNC1 в начале кода как GS (`LEADING_GS_RE` API). */
// eslint-disable-next-line no-control-regex -- GS (FNC1) и есть искомый символ
const LEADING_GS_RE = /^\x1d+/;

/** Идентификатор символики, который добавляют некоторые сканеры (`]d2`). */
const SYMBOLOGY_ID_RE = /^\][A-Za-z]\d/;

/** Внутренний код фасовки: `CHB` + 9 цифр + цифра Луна (W17-1). */
const INTERNAL_CODE_RE = /^CHB(\d{9})(\d)$/;

/** Допустимые длины GTIN (GS1). */
const GTIN_LENGTHS: readonly number[] = [8, 12, 13, 14];

/**
 * Адрес ячейки или уровня, набранный руками без префикса: `A`, `A-02`,
 * `a-2-3`. Используется только полем ячейки (`toBinScanRaw`) — в роутер
 * `parseScan` не входит: там это свободный текст, как на сервере.
 */
const BARE_BIN_ADDRESS_RE = /^[A-Za-z](-\d{1,2}){0,2}$/;

/** Почему скан с нашим префиксом не принят. */
export type ScanInvalidReason =
  | "internal_code_invalid"
  | "lot_code_invalid"
  | "expiry_invalid"
  | "empty_value";

/** Результат разбора скана. */
export type ScanResult =
  | { kind: "bin"; address: string }
  | { kind: "label"; internalCode: string; lotCode?: string; expiresAt?: string }
  | { kind: "sku"; internalCode: string }
  | { kind: "lot"; lotCode: string }
  | { kind: "box"; value: string }
  | { kind: "order"; value: string }
  | { kind: "crpt"; gtin: string | null; raw: string }
  | { kind: "gtin"; gtin: string }
  | { kind: "invalid"; raw: string; reason: ScanInvalidReason }
  | { kind: "text"; value: string };

/** Контрольная цифра Луна — как SQL `chb_luhn_check_digit` на сервере. */
export function luhnCheckDigit(digits: string): string {
  let total = 0;
  for (let i = 1; i <= digits.length; i += 1) {
    let d = Number(digits[digits.length - i]);
    if (i % 2 === 1) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    total += d;
  }
  return String((10 - (total % 10)) % 10);
}

/** Привести ввод к виду хранения: без пробелов и дефисов, верхний регистр. */
export function normalizeInternalCode(input: string): string {
  return input.replace(/[\s-]+/g, "").toUpperCase();
}

/** Строка — корректный внутренний код (формат + контрольная цифра). */
export function isValidInternalCode(code: string): boolean {
  const match = INTERNAL_CODE_RE.exec(code);
  if (!match) return false;
  return luhnCheckDigit(match[1]!) === match[2];
}

/** Код для показа человеку: `CHB000000123-7` (дефис перед контрольной). */
export function displayInternalCode(code: string): string {
  if (!INTERNAL_CODE_RE.test(code)) return code;
  return `${code.slice(0, -1)}-${code.slice(-1)}`;
}

/** GTIN длины 8/12/13/14 с верной контрольной цифрой GS1 mod 10. */
export function isValidGtin(s: string): boolean {
  if (!/^\d+$/.test(s) || !GTIN_LENGTHS.includes(s.length)) return false;
  const body = s.slice(0, -1);
  let total = 0;
  for (let i = 1; i <= body.length; i += 1) {
    const d = Number(body[body.length - i]);
    total += i % 2 === 1 ? d * 3 : d;
  }
  return String((10 - (total % 10)) % 10) === s.slice(-1);
}

/** Разобрать скан по W17-3 (см. шапку файла). */
export function parseScan(raw: string): ScanResult {
  const value = raw.trim();
  const upper = value.toUpperCase();

  if (upper.startsWith(SCAN_PREFIX.LOC)) {
    const address = upper.slice(SCAN_PREFIX.LOC.length).trim();
    if (!address) return { kind: "invalid", raw: value, reason: "empty_value" };
    return { kind: "bin", address };
  }
  if (upper.startsWith(SCAN_PREFIX.SKU)) {
    return value.includes(PAYLOAD_SEPARATOR) ? parseLabel(value) : parseSku(value);
  }
  if (upper.startsWith(SCAN_PREFIX.LOT)) {
    const lotCode = upper.slice(SCAN_PREFIX.LOT.length).trim();
    if (!LOT_CODE_RE.test(lotCode)) {
      return { kind: "invalid", raw: value, reason: lotCode ? "lot_code_invalid" : "empty_value" };
    }
    return { kind: "lot", lotCode };
  }
  if (upper.startsWith(SCAN_PREFIX.BOX)) {
    return { kind: "box", value: value.slice(SCAN_PREFIX.BOX.length).trim() };
  }
  if (upper.startsWith(SCAN_PREFIX.ORD)) {
    return { kind: "order", value: value.slice(SCAN_PREFIX.ORD.length).trim() };
  }

  const crpt = parseCrpt(raw);
  if (crpt) return crpt;

  if (isValidGtin(value)) return { kind: "gtin", gtin: value };
  return { kind: "text", value };
}

/**
 * Вид скана — «код товара» (фасовка, этикетка, партия, ЧЗ, GTIN): такой ввод
 * всегда отправляется на сервер, а не остаётся фильтром списка.
 */
export function isGoodsScan(result: ScanResult): boolean {
  return (
    result.kind === "sku" ||
    result.kind === "label" ||
    result.kind === "lot" ||
    result.kind === "crpt" ||
    result.kind === "gtin"
  );
}

/**
 * Строка для резолвера поля ячейки: адрес без префикса (`A-02`, `a-2-3`),
 * набранный руками, превращаем в `LOC:A-02` — иначе сервер понял бы его как
 * поиск по артикулу. Всё остальное отправляем как есть.
 */
export function toBinScanRaw(input: string): string {
  const value = input.trim();
  if (BARE_BIN_ADDRESS_RE.test(value)) return `${SCAN_PREFIX.LOC}${value.toUpperCase()}`;
  return value;
}

/** `SKU:<код>` без партии — Code128 этикетки или ручной ввод. */
function parseSku(value: string): ScanResult {
  const internalCode = normalizeInternalCode(value.slice(SCAN_PREFIX.SKU.length));
  if (!internalCode) return { kind: "invalid", raw: value, reason: "empty_value" };
  if (!isValidInternalCode(internalCode)) {
    return { kind: "invalid", raw: value, reason: "internal_code_invalid" };
  }
  return { kind: "sku", internalCode };
}

/**
 * Нагрузка этикетки: сегменты в любом порядке, неизвестные игнорируются.
 * Без партии и без срока — это просто скан фасовки.
 */
function parseLabel(value: string): ScanResult {
  let internalCode: string | null = null;
  let lotCode: string | undefined;
  let expiresAt: string | undefined;

  for (const segment of value.split(PAYLOAD_SEPARATOR)) {
    const upper = segment.trim().toUpperCase();
    if (upper.startsWith(SCAN_PREFIX.SKU)) {
      internalCode = normalizeInternalCode(upper.slice(SCAN_PREFIX.SKU.length));
    } else if (upper.startsWith(SCAN_PREFIX.LOT)) {
      const lot = upper.slice(SCAN_PREFIX.LOT.length).trim();
      if (!lot) continue;
      if (!LOT_CODE_RE.test(lot)) return { kind: "invalid", raw: value, reason: "lot_code_invalid" };
      lotCode = lot;
    } else if (upper.startsWith(EXP_PREFIX)) {
      const exp = upper.slice(EXP_PREFIX.length).trim();
      // Пустой EXP: — «срок не указан» (W17-9a), это не ошибка.
      if (!exp) continue;
      if (!isValidIsoDate(exp)) return { kind: "invalid", raw: value, reason: "expiry_invalid" };
      expiresAt = exp;
    }
  }

  if (!internalCode) return { kind: "invalid", raw: value, reason: "empty_value" };
  if (!isValidInternalCode(internalCode)) {
    return { kind: "invalid", raw: value, reason: "internal_code_invalid" };
  }
  if (lotCode === undefined && expiresAt === undefined) return { kind: "sku", internalCode };
  return {
    kind: "label",
    internalCode,
    ...(lotCode !== undefined ? { lotCode } : {}),
    ...(expiresAt !== undefined ? { expiresAt } : {}),
  };
}

/** «Честный знак»: GS-строка или AI 01 + 14 цифр (с префиксом символики). */
function parseCrpt(raw: string): ScanResult | null {
  // Ведущие GS срезаем до и после идентификатора символики, иначе AI 01 не найдётся.
  const body = raw
    .trim()
    .replace(LEADING_GS_RE, "")
    .replace(SYMBOLOGY_ID_RE, "")
    .replace(LEADING_GS_RE, "");
  const ai01 = /^01(\d{14})/.exec(body);
  if (ai01) return { kind: "crpt", gtin: ai01[1]!, raw };
  if (raw.includes(GS)) return { kind: "crpt", gtin: null, raw };
  return null;
}

/** Календарная дата YYYY-MM-DD, реально существующая (не 2027-02-30). */
function isValidIsoDate(s: string): boolean {
  if (!EXP_RE.test(s)) return false;
  const date = new Date(`${s}T00:00:00.000Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === s;
}
